import { createContext } from "react";

type BasketData = App.Data.Partner.PartnerBasketData;
type CarCard = App.Data.Partner.PartnerCarCardData;

/**
 * What a page may ask the partner portal's global overlays to do
 * (spec/partner-portal-plan.md §17.1): open the car detail drawer on a
 * listing — optionally with the ordered ids of the list it came from, so
 * ←/→ walk the neighbours — close it, toggle a heart, and drive the basket
 * (§8.1) and the checkout (§8.2). The offer overlay joins in Phase 3.
 */
export interface PartnerOverlays {
    /** The listing the drawer shows, or null while closed. */
    detailListingId: number | null;
    openCarDetail: (listingId: number, listIds?: readonly number[]) => void;
    closeCarDetail: () => void;
    /**
     * Whether the contact likes the listing right now: the page's own value
     * until the heart is toggled, then the optimistic one.
     */
    isLiked: (listingId: number, fallback: boolean) => boolean;
    /** Optimistically toggle the heart and persist it. */
    toggleLike: (listingId: number, currentlyLiked: boolean) => void;

    /** The header pill's count — the shared prop plus this session's optimistic moves. */
    basketCount: number;
    /** Whether the listing sits in the basket: the page's value until it is added or removed here. */
    isInBasket: (listingId: number, fallback: boolean) => boolean;
    /** Put a car in the basket (optimistic count, toast) — a car already in it opens the drawer instead. */
    addToBasket: (car: CarCard) => void;
    /** Take a line out of the basket. */
    removeFromBasket: (lineId: number) => void;
    /** Empty the basket in one go (`POST partner.basket.clear`, §8.1) — the drawer asks first. */
    clearBasket: () => void;
    /** The drawer's re-priced payload once loaded; null before the first open. */
    basket: BasketData | null;
    basketLoading: boolean;
    basketOpen: boolean;
    openBasket: () => void;
    closeBasket: () => void;
    checkoutOpen: boolean;
    /** Promote the basket drawer to the checkout sheet. */
    openCheckout: () => void;
    closeCheckout: () => void;
    /** Re-fetch the basket (after a checkout, or a failed one that flagged lines). */
    reloadBasket: () => void;

    /**
     * Open the offer dialog on a listing (spec/partner-portal-plan.md §7).
     * Pass the card when the caller has it; otherwise the dialog fetches it.
     */
    openOffer: (listingId: number, car?: CarCard) => void;
    closeOffer: () => void;
    /** The listing the offer dialog is open on, or null while closed. */
    offerListingId: number | null;
    /**
     * Bumps on every offer move this session saw — submitted, accepted,
     * withdrawn, or broadcast on the account channel — so a drawer showing
     * the car re-fetches its banner.
     */
    offerVersion: number;
    /** Accept our counter, decline it, or withdraw the bid; resolves to whether it went through. */
    actOnOffer: (lineId: number, action: OfferAction) => Promise<boolean>;
}

export type OfferAction = "accept" | "decline" | "withdraw";

const inert: PartnerOverlays = {
    detailListingId: null,
    openCarDetail: () => undefined,
    closeCarDetail: () => undefined,
    isLiked: (_listingId, fallback) => fallback,
    toggleLike: () => undefined,
    basketCount: 0,
    isInBasket: (_listingId, fallback) => fallback,
    addToBasket: () => undefined,
    removeFromBasket: () => undefined,
    clearBasket: () => undefined,
    basket: null,
    basketLoading: false,
    basketOpen: false,
    openBasket: () => undefined,
    closeBasket: () => undefined,
    checkoutOpen: false,
    openCheckout: () => undefined,
    closeCheckout: () => undefined,
    reloadBasket: () => undefined,
    openOffer: () => undefined,
    closeOffer: () => undefined,
    offerListingId: null,
    offerVersion: 0,
    actOnOffer: () => Promise.resolve(false),
};

/** Defaults to an inert set so a component outside the provider still renders. */
export const PartnerOverlaysContext = createContext<PartnerOverlays>(inert);
